import Env from '@/infra/env'
import { type ActivityFeatureOptions } from '@/features/activity'
import { NotificationApiRoot } from '@/features/activity/api/notification-api'
import { NotificationStore } from '@/features/activity/NotificationStore'
import { ActivityViewModel } from '@/features/activity/ActivityView/ActivityViewModel'
import { defer, distinctUntilChanged, merge, switchMap } from 'rxjs'
import { shareLatest, withAbortSignal } from '@/utils/rx-util'
import { toStream } from '@/utils/mobx-util'
import { NotificationPreferencesStore } from '@/features/activity/NotificationPreferencesStore'
import { NotificationPreferencesViewModel } from '@/features/activity/NotificationPreferencesViewModel'
import { DesktopNotificationStore } from '@/features/activity/DesktopNotificationStore'
import { take } from 'rxjs/operators'
import { notificationsToSystemNotifications } from '@/features/activity/notifications-to-system-notifications'

export function InternalActivityFeature(options: ActivityFeatureOptions) {
  const notificationApi = new NotificationApiRoot({
    token$: options.token$,
    baseURL: Env.NOTIFICATION_API,
  })

  const preferencesStore = new NotificationPreferencesStore(
    notificationApi,
    options.getCurrentMember,
  )

  const notificationStore = new NotificationStore(
    options.getCurrentMember,
    options.primeUser,
    options.primeWorkspace,
    notificationApi,
  )

  const desktopNotificationStore = new DesktopNotificationStore(
    options.token$.pipe(take(1)),
  )

  // The activity VM is a singleton since we want to preserve
  // the loaded notifications.
  const activityVm = new ActivityViewModel(
    notificationStore,
    options.createSnackbarRetryWrapper,
    options.createSnackbarErrorHandler,
  )

  const preferencesVm = new NotificationPreferencesViewModel(
    preferencesStore,
    options.newSnackbar,
  )

  // We'll expose a way to subscribe to the unread notification
  // count; when subscribed to, we'll fetch the first page of notifications
  // via the Activities VM above, so when the user navigates there, the content
  // is already loaded.
  const unreadNotificationsCount$ = defer(() =>
    withAbortSignal(activityVm.fetchNotifications),
  ).pipe(
    switchMap(() =>
      toStream(() => notificationStore.unreadNotificationsCount, true),
    ),
    distinctUntilChanged(),
    shareLatest(),
  )

  // Wire up desktop notifications.
  merge(
    options.systemNotifications$,
    notificationsToSystemNotifications(
      notificationStore.newNotifications$,
      (n) => notificationStore.markRead([n]),
      options.navigate,
    ),
  ).subscribe(desktopNotificationStore.show)

  return {
    // Internal
    getCurrentUser: options.getCurrentUser,
    notificationStore,
    preferencesStore,
    desktopNotificationStore,
    activityVm,
    preferencesVm,
    unreadNotificationsCount$,
  }
}
